import itemgroupService from '../../../services/itemgroupService';
import filterService from '../../../services/filterService';

import {
    setItemGroupLoading,
    setItemGroupError,
    setItemGroupList,
    setItemGroupCurrent,
    setItemGroupPagination,
    setItemGroupSort,
    setItemGroupFilters,
    setItemGroupDefaultFilter,
    setItemGroupSettings,
    clearItemGroupCurrent,
    clearItemGroupError,
    setItemGroupFilterLoading,
    changeItemGroupFilterCondition,
    addItemGroupFilterCondition,
    updateItemGroupFilterConditionRule,
    updateItemGroupFilterConditionValue,
    removeItemGroupFilterCondition,
    resetItemGroupFiltersAndPagination
} from './itemgroupSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().itemgroup.isSettingsInitialized) {
            dispatch(setItemGroupLoading(true));
            const response = await itemgroupService.getSettings();

            // Set the settings
            dispatch(setItemGroupSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setItemGroupDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setItemGroupError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setItemGroupLoading(false));
    }
};

export const fetchItemgroups = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().itemgroup;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setItemGroupLoading(true));
        const response = await itemgroupService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setItemGroupList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchItemgroups:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setItemGroupError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setItemGroupLoading(false));
    }
};

export const fetchItemGroup = (id) => async (dispatch) => {
    try {
        dispatch(setItemGroupLoading(true));
        const response = await itemgroupService.getOne(id);
        dispatch(setItemGroupCurrent(response.data));
    } catch (error) {
        dispatch(setItemGroupError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setItemGroupLoading(false));
    }
};

export const createItemGroup = (data) => async (dispatch) => {
    try {
        const response = await itemgroupService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateItemGroup = (id, data) => async (dispatch) => {
    try {
        const response = await itemgroupService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteItemGroup = (id) => async (dispatch) => {
    try {
        dispatch(setItemGroupLoading(true));
        const response = await itemgroupService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setItemGroupError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setItemGroupLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setItemGroupPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setItemGroupSort(sort));
};

export const searchItemgroups = (query) => (dispatch, getState) => {
    const currentFilters = getState().itemgroup.filters;
    dispatch(setItemGroupFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setItemGroupDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeItemGroupFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addItemGroupFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateItemGroupFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateItemGroupFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeItemGroupFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setItemGroupFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().itemgroup.settings;
            dispatch(setItemGroupSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setItemGroupFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setItemGroupFilterLoading(false));
        dispatch(setItemGroupError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetItemGroupFiltersAndPagination());
};